import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    // Check if backend URL is configured
    if (!process.env.REACT_APP_BACKEND_URL) {
      setTimeout(() => {
        setIsSubscribed(true);
        toast.success('Welcome to the Chronos family');
        setEmail('');
        setIsLoading(false);
      }, 1000);
      return;
    }

    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      if (response.data.success) {
        setIsSubscribed(true);
        toast.success('Welcome to the Chronos family');
        setEmail('');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-obsidian border-t border-white/5" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-8"
        >
          {/* Header */}
          <div className="space-y-4">
            <p className="text-xs uppercase tracking-[0.3em] text-gold-500 font-sans">Newsletter</p>
            <h2 className="text-4xl md:text-5xl font-serif text-white">
              Join the <span className="italic text-gold-400">Inner Circle</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto">
              Be the first to discover new collections, exclusive events, and insights into the world of haute horlogerie.
            </p>
          </div>

          {/* Form */}
          {isSubscribed ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center gap-3 py-6"
            >
              <div className="w-12 h-12 bg-gold-500 flex items-center justify-center">
                <Check className="w-6 h-6 text-black" />
              </div>
              <p className="text-white font-serif text-xl">You're on the list</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold-500/50 focus:ring-0 rounded-none h-14 px-6"
                required
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-gold-500 text-black hover:bg-gold-400 rounded-none px-8 h-14 text-sm uppercase tracking-widest font-bold transition-all hover:scale-[1.02] btn-glow"
                data-testid="newsletter-submit-btn"
              >
                {isLoading ? (
                  <span className="animate-pulse">Subscribing...</span>
                ) : (
                  <>
                    Subscribe
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Privacy Note */}
          <p className="text-xs text-white/30">
            By subscribing, you agree to receive communications from Chronos. We respect your privacy.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
